import { useEffect, useState, useCallback } from "react";
import {
  createBondCharacteristic,
  createBondLadder,
  editOrder,
  fetchAccounts,
  fetchBondCalcSettlementDate,
  placeOrder,
  placeListOrder, // List RFQ - one enquiry over several bonds
  modifyRfq, // NEW
  allocateTrade, // NEW
} from "../../http/api";
import {
  PROTOCOL_TYPES,
  ORDER_SIDES,
  USER_ROLES,
  MKTX_INQUIRY_TYPES,
  MKTX_DEALER_LIST_MODES,
  MKTX_DEALER_LIST_MODE_DISPLAY_TO_SUBMIT,
  MKTX_DEALER_LIST_MODE_SUBMIT_TO_DISPLAY,
  MKTX_STRATEGY_TYPES,
  TRUMID_PARTIAL_EXECUTION,
  MKTX_DEFAULT_EXPIRY_TIME_UTC,
  MAX_LIST_RFQ_NAME_LENGTH,
} from "../../Constants/AppConstants";
import { STAGING_FLAG_KEY, STAGING_FLAG_VALUES } from "../../Constants/stagingBlotter";
import { useAuth } from "../../Context/AuthProvider";
import { useActualOrderStore } from "../../Store/actualOrderStore";
import { useBondCharacteristicStore } from "../../Store/bondCharacteristicStore";
import { useBondLadderStore } from "../../Store/bondLadderStore";
import { useListRfqStore, LIST_RFQ_TAB_INDEX } from "../../Store/listRfqStore";
import { buildListOrderRequest, validateListRfq } from "./listRfqPayload";
import { bondTypes, fieldOptions, isTradingDeskSelected, loadStrategyParamDefs, updateVenues, filterVenueOptions } from "./optionsHelper";
import { filterObjectByBondType } from "../../Utils/bondTypeFieldFilter";
import { toBackendFilterKeys } from "../../Utils/filterKeyAliases";
import { jwtDecode } from "jwt-decode";
import { deriveParamMeta, validateParamValue } from "./strategyParamUtils";

export const normalizeYesNoToYN = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const s = String(val).trim().toLowerCase();
  if (s === "yes" || s === "y") return TRUMID_PARTIAL_EXECUTION.YES;
  if (s === "no" || s === "n") return TRUMID_PARTIAL_EXECUTION.NO;
  return val;
};

const parseUtcMs = (val) => {
  if (val === null || val === undefined || val === "") return NaN;
  const str = String(val).trim();
  const withZ = /[zZ]|[+\-]\d{2}:\d{2}$/.test(str) ? str : `${str}Z`;
  const d = new Date(withZ);
  return isNaN(d.getTime()) ? NaN : d.getTime();
};

const toUtcIsoNoZ = (ms) => new Date(ms).toISOString().slice(0, -1);

// Builds the default expiry ISO string for the current day using MKTX_DEFAULT_EXPIRY_TIME_UTC constant
const getSameDayMarketAxessExpiryIso = () => {
  const [timePart] = (MKTX_DEFAULT_EXPIRY_TIME_UTC || "20:57:00.000").split(".");
  const [hh, mm, ss] = timePart.split(":").map(Number);
  const msStr = (MKTX_DEFAULT_EXPIRY_TIME_UTC || "20:57:00.000").split(".")[1] || "0";
  const now = new Date();
  const expiryUtc = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    hh,
    mm,
    ss,
    Number(msStr)
  );
  return toUtcIsoNoZ(expiryUtc);
};

const getDefaultEffectiveFromExpiry = (expiryIso) => {
  const nowMs = Date.now();
  const expiryMs = parseUtcMs(expiryIso);
  const effectiveMs = Number.isFinite(expiryMs) ? Math.min(nowMs, expiryMs) : nowMs;
  return toUtcIsoNoZ(effectiveMs);
};

export const useOrderEntry = ({
  source = "navbar",
  secondarySource,
  initialData,
  cusip = "",
  side = "",
  qty = "",
  onSubmitSuccess,
  onClose,
}) => {
  const { token } = useAuth();
  const decodedToken = jwtDecode(token);
  const tokenRole = decodedToken.role;
  const isLadderLevelSendToMarket = secondarySource === "ladder-level-send-to-market";

  // State management
  const [isCusipValid, setIsCusipValid] = useState(false);
  const [bondDetailsMain, setBondsDetailsMain] = useState(null);
  const [showBondsDetails, setShowBondsDetails] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [accountIdentifiers, setAccountIdentifiers] = useState([]);
  const [error, setError] = useState(null);
  const [lastErrorField, setLastErrorField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const { userId } = useAuth();
  // Store hooks
  const {
    actualOrderData,
    setActualOrderData,
    fieldErrors: orderFieldErrors,
    setFieldErrors: setOrderFieldErrors,
    clearFieldErrors: clearOrderFieldErrors,
    resetActualOrderData,
  } = useActualOrderStore();

  const {
    bondCharacteristicData,
    setCurrentCharacteristicID,
    fieldErrors: bondCharacteristicErrors,
    setFieldErrors: setBondCharacteristicErrors,
    clearFieldErrors: clearBondCharacteristicErrors,
    resetBondCharacteristicId,
    resetBondCharacteristicData,
    validationErrors: bondCharacteristicValidationErrors, // Add validation errors
  } = useBondCharacteristicStore();

  const {
    bondLadderData,
    fieldErrors: bondLadderErrors,
    setFieldErrors: setBondLadderErrors,
    clearFieldErrors: clearBondLadderErrors,
    resetBondLadderData,
    validationErrors: bondLadderValidationErrors, // Add validation errors
  } = useBondLadderStore();

  // Clear all data function
  const clearDataInAllTabs = useCallback(() => {
    resetActualOrderData();
    resetBondCharacteristicData();
    resetBondLadderData();
    // The List RFQ tab is emptied with the rest, so "Clear All", closing the modal and a
    // successful send all leave it blank - the same lifecycle the other three tabs have.
    // Read off the store directly rather than subscribing: this callback only ever writes.
    useListRfqStore.getState().resetListRfq();
    clearOrderFieldErrors();
    clearBondCharacteristicErrors();
    clearBondLadderErrors();
    setShowBondsDetails(false);
    setShowConfirmation(false);
    setError(null);
    setIsCusipValid(false);
    setBondsDetailsMain(null);
  }, [
    resetActualOrderData,
    resetBondCharacteristicData,
    resetBondLadderData,
    clearOrderFieldErrors,
    clearBondCharacteristicErrors,
    clearBondLadderErrors,
  ]);

  // Handle clear all action
  const handleClearAll = useCallback(() => {
    // Clear All empties what the user filled in — it does not change what the order is for.
    // An order raised against a characteristic keeps its characteristic and, when it belongs
    // to a rung, its ladder linkage, or clearing would quietly turn it into a plain order.
    // The account and side those fix are kept with them: side is locked to the
    // characteristic, and the account is the one the characteristic was placed on.
    // Teardown paths (modal close, post-submit) call clearDataInAllTabs directly and still
    // clear everything.
    const { characteristicId, bondLadderId, bondLadderRungId, account, side, settlementDate } =
      useActualOrderStore.getState().actualOrderData || {};
    // The List Order tab keeps its own copy of the same date, in its own store.
    const { settlementDate: listSettlementDate } =
      useListRfqStore.getState().listRfqData || {};

    clearDataInAllTabs();

    // Settlement date survives Clear All, on both tabs that show it.
    //
    // In plain terms: it is not something the trader typed, so there is nothing to clear.
    // It is calculated from the trade date by fetchBondCalcSettlementDate, shown read-only,
    // and re-fetched at submit either way (handleBondOrderSubmit and handleListRfqSubmit
    // both overwrite it with a fresh value immediately before sending) — so the value put
    // back here is display only and can never reach the wire stale.
    //
    // Restoring it rather than letting it re-fetch matters for more than the flicker: each
    // tab only asks for the date when it has none (ActualOrderTab's loadSettlementDate
    // effect and the matching one in ListRfqTab), so the box sat empty until a round trip
    // came back — and on the Bond Order tab an empty settlement date disables Send outright
    // for a Trumid RFQ (OrderEntryCore.isMissingSettlement). The List Order tab is worse: it
    // is unmounted whenever another tab is open (TabPanel renders its children only while
    // selected), so its effect cannot run to replace the value until the trader goes back to
    // it.
    //
    // Only these two stores are touched because only these two tabs show the field — Bond
    // Characteristic and Bond Ladder hold no settlement date at all.
    if (settlementDate) setActualOrderData({ settlementDate });
    if (listSettlementDate) {
      useListRfqStore.getState().setListRfqData({ settlementDate: listSettlementDate });
    }

    if (characteristicId) {
      setActualOrderData({
        characteristicId,
        bondLadderId: bondLadderId ?? null,
        bondLadderRungId: bondLadderRungId ?? null,
        account: account ?? "",
        side: side ?? "",
      });
    }
  }, [clearDataInAllTabs, setActualOrderData]);

  // Effects
  useEffect(() => {
    setShowBondsDetails(!!bondDetailsMain);
  }, [bondDetailsMain]);

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const response = await fetchAccounts(token);
        const apiIdentifiers = response.map(
          (account) => account.accountIdentifier
        );
        setAccountIdentifiers(Array.from(new Set(apiIdentifiers)));
      } catch (error) {
        console.error("Error fetching accounts:", error);
      }
    };
    loadAccounts();
  }, [token]);

  // Handle initial data population
  useEffect(() => {
    const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== "";

    if (source === "sendToMarket" && initialData) {
      const firstLadderOrder = Array.isArray(initialData.ladderOrders) && initialData.ladderOrders.length > 0
        ? initialData.ladderOrders[0]
        : Array.isArray(initialData.orders) && initialData.orders.length > 0
        ? initialData.orders[0]
        : null;
      const ladderCharacteristics = (() => {
        if (!initialData.ladderCharacteristics) return {};
        try {
          return typeof initialData.ladderCharacteristics === "string"
            ? JSON.parse(initialData.ladderCharacteristics)
            : initialData.ladderCharacteristics;
        } catch {
          return {};
        }
      })();
      const stagedFlag = initialData[STAGING_FLAG_KEY] ?? firstLadderOrder?.[STAGING_FLAG_KEY];
      const newData = {
        account: initialData.account || firstLadderOrder?.account || "",
        bondInventoryId: isLadderLevelSendToMarket ? null : initialData.bondInventoryId ?? null,
        bondLadderId: initialData.bondLadderId ?? null,
        // carry the rung forward so re-submitting an existing ladder order keeps its rung link
        bondLadderRungId: isLadderLevelSendToMarket
          ? null
          : initialData.bondLadderRungId ?? firstLadderOrder?.bondLadderRungId ?? null,
        bondType: initialData.bondType || ladderCharacteristics.bondType || firstLadderOrder?.bondType || "",
        characteristicId: initialData.characteristicId ?? null,
        comments: initialData.comments || "",
        listName: isLadderLevelSendToMarket ? initialData.listName || "" : "",
        cusip: isLadderLevelSendToMarket ? "" : initialData.cusip || "",
        ladderReferenceCusip: firstLadderOrder?.cusip || "",
        ladderReferencePrice: firstLadderOrder?.price ?? "",
        // pm: initialData.pm || "",
        // pmId: initialData.pmId ?? null,
        price: isLadderLevelSendToMarket ? "" : initialData.price ?? "",
        protocol: initialData.protocol || firstLadderOrder?.protocol || "CLOB",
        qty: isLadderLevelSendToMarket ? "" : initialData.qty ?? initialData.quantity ?? "",
        side: initialData.side || initialData.action || firstLadderOrder?.side || firstLadderOrder?.action || "",
        venue: initialData.venue || firstLadderOrder?.venue || "",
        // Carry the staging flag across. Without it the store holds no orderVersion, so
        // isStagingOrder() reads Number(undefined) === 0 -> false and the screen treats a
        // staged order as live: the dealer field shows the order's own inventory dealer and
        // every guard that asks "has a dealer actually been chosen?" (OrderEntryCore's
        // isStaged -> isDealerSelectionBlocked, and the venue-nulling guard below) answers
        // yes. Only spread when a value exists, so a payload genuinely lacking the field
        // behaves exactly as before rather than defaulting to staged.
        ...(stagedFlag !== undefined ? { [STAGING_FLAG_KEY]: stagedFlag } : {}),
        ttl: (initialData.protocol || firstLadderOrder?.protocol) === "RFQ" ? initialData.ttl || firstLadderOrder?.ttl || 300 : null,
        secondarySource,
      };
      if (initialData.mktxDealerListMode) {
        newData.mktxDealerListMode =
          MKTX_DEALER_LIST_MODE_SUBMIT_TO_DISPLAY[initialData.mktxDealerListMode] ||
          initialData.mktxDealerListMode;
      }
      setActualOrderData(newData);
      return;
    }

    if (source === "maInventoryAddOrder") {
      const newData = {
        protocol: PROTOCOL_TYPES.MARKETAXESS,
        side: ORDER_SIDES.BUY,
        maInventoryBidDealer: initialData?.maInventoryBidDealer ?? "",
        maInventoryBidQuantity: initialData?.maInventoryBidQuantity ?? "",
        maInventoryBidPrice: initialData?.maInventoryBidPrice ?? "",
        maInventoryOfferPrice: initialData?.maInventoryOfferPrice ?? "",
        maInventoryOfferQuantity: initialData?.maInventoryOfferQuantity ?? "",
        maInventoryOfferDealer: initialData?.maInventoryOfferDealer ?? "",
        maInventoryDealers: initialData?.maInventoryDealers || [],
      };

      if (hasValue(initialData?.cusip)) {
        newData.cusip = initialData.cusip;
      }

      if (hasValue(initialData?.qty ?? initialData?.quantity)) {
        newData.qty = initialData.qty ?? initialData.quantity;
      }

      if (hasValue(initialData?.price)) {
        newData.price = initialData.price;
      }

      setActualOrderData(newData);
    }
  }, [source, secondarySource, initialData, setActualOrderData, isLadderLevelSendToMarket]);

  // Handle passed props
  useEffect(() => {
    if (cusip || side || qty) {
      const updates = {};
      if (cusip && cusip !== actualOrderData.cusip) updates.cusip = cusip;
      if (side && side !== actualOrderData.side) updates.side = side;
      if (qty && qty !== actualOrderData.qty) updates.qty = qty;

      if (Object.keys(updates).length > 0) {
        setActualOrderData({ ...actualOrderData, ...updates });
      }
    }
  }, [cusip, side, qty, actualOrderData, setActualOrderData]);

  // Helper functions
  const mapCheckboxField = (field) => {
    if (field === "couponMin" || field === "couponMax") return "couponRange";
    if (field === "maturityMin" || field === "maturityMax") return "maturityRanges";
    if (field === "priceMin" || field === "priceMax") return "price";
    if (field === "yieldMin" || field === "yieldMax") return "yield";
    return field;
  };

  const isValidField = (field, validFields) => {
    if (validFields.includes(field)) return true;
    if (field.endsWith("Min") || field.endsWith("Max")) {
      const baseField = field.replace(/Min$|Max$/, "");
      if (baseField === "coupon" && validFields.includes("couponRange")) return true;
      if (baseField === "maturity" && validFields.includes("maturityRanges")) return true;
      if (validFields.includes(baseField)) return true;
    }
    const relatedFieldMappings = { durationComparison: "duration" };
    return (
      relatedFieldMappings[field] && validFields.includes(relatedFieldMappings[field])
    );
  };

  const trimObjectValues = (obj) => {
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = typeof obj[key] === "string" ? obj[key].trim() : obj[key];
      return acc;
    }, {});
  };

  // add helper to pick allowed fields
  const pick = (obj, keys) =>
    keys.reduce((acc, k) => {
      if (obj[k] !== undefined) acc[k] = obj[k];
      return acc;
    }, {});

  // FILL/ADJUST THIS LIST to the exact fields backend should receive
  const ORDER_FIELDS_TO_SEND = [
    "account",
    "bondInventoryId",
    "bondLadderId",
    "bondLadderRungId",
    "characteristicId",
    "comments",
    "cusip",
    "price",
    "protocol",
    "qty",
    "side",
    "ttl",
    "venue",
    // Staging flag. MUST stay on this list: pick() is an allowlist, so omitting the key
    // here silently drops it from every create and modify payload, and the backend then
    // falls back to inferring staging from the venue string. See Constants/stagingBlotter.js.
    STAGING_FLAG_KEY,
    // Shared RFQ fields (used by both TruMid RFQ and MarketAxess)
    "rfqInquiryType",
    "settlementDate",
    "priceType",
    "stipulationType",
    "allowPartialExecution",
    "quoteSubmissionDeadline",
    // MarketAxess specific fields
    "mktxDealerListMode",
    "mktxTargetLevel",
    "mktxValidSeconds",
    // New: MarketAxess pricing process
    "mktxPricingProcess",
    "strategyType",
    "effectiveTime",
    "expiryTime",
    "strategyParams", // renamed payload field (array of {paramId,value})
    "rfqId",          // NEW: carry rfqId for edit RFQ
    "orderId",
  ];

  // Allowed fields for RFQ modify payload (PATCH)
  const MODIFY_RFQ_FIELDS = [
    "qty",
    "side",
    "mktxTargetLevel",
    "strategyType",
    "effectiveTime",
    "expiryTime",
    "strategyParams",
  ];

  // Bond submission handler
  const processBondSubmission = async ({
    data,
    storeChecked,
    panelName,
    createBond,
    errorMsgMissing,
    requiredFields,
  }) => {
    const { side, account, qty, bondType, pm, pmId, ...otherData } = data;
    const allRequiredFields = [...requiredFields];

    if (allRequiredFields.some((field) => !data[field])) {
      console.error(errorMsgMissing);
      setError(errorMsgMissing);
      return false;
    }

    const validFields = bondTypes[bondType]?.fields || [];
    const characteristics = {};

    Object.entries(otherData).forEach(([field, value]) => {
      const isRequired = requiredFields.includes(field);
      const checkboxField = mapCheckboxField(field);
      const isChecked = isRequired
        ? true
        : storeChecked[panelName]?.[checkboxField] || false;

      if (
        isChecked &&
        value !== undefined &&
        value !== "" &&
        isValidField(field, validFields)
      ) {
        characteristics[field] = value;
      }
    });

    // NEW: final safety filter against bondType (removes stale fields after type switch)
    const filteredCharacteristics = filterObjectByBondType(bondType, characteristics);

    // Serialize securityCharge: ["ALL"] → "ALL" string; specific array stays as-is
    if (Array.isArray(filteredCharacteristics.securityCharge)) {
      if (filteredCharacteristics.securityCharge.includes("ALL")) {
        filteredCharacteristics.securityCharge = "ALL";
      }
    }

    const orderObject = {
      ...requiredFields.reduce((acc, field) => {
        acc[field] = data[field];
        return acc;
      }, {}),
      type: bondType,
      comments: data.comments,
      // Stored under the spelling the backend reads: this JSON is replayed through the
      // filter API later, and only minCoupon/minMaturity/... are read there
      characteristics: JSON.stringify(toBackendFilterKeys(filteredCharacteristics)),
      // pm: data.pm,
      // pmId: data.pmId,
      userId: userId || null,
    };

    setLoading(true);
    setError(null);

    const loadingTimeout = setTimeout(() => {
      setError("Request failed to initiate. Please try again.");
      setLoading(false);
    }, 10000);

    try {
      await createBond(token, orderObject, loadingTimeout);

      clearDataInAllTabs();
      if (onClose) onClose();
      return true;
    } catch (error) {
      console.error(`Failed to create ${panelName.replace('bond', 'bond ')}:`, error);
      setError(error.message || `Failed to create ${panelName.replace('bond', 'bond ')}. Please try again.`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check for validation errors in stores
  const hasStoreValidationErrors = (activeTab) => {
    if (activeTab === 1) { // Bond Characteristic tab
      return Object.values(bondCharacteristicValidationErrors).some(Boolean);
    } else if (activeTab === 2) { // Bond Ladder tab
      return Object.values(bondLadderValidationErrors).some(Boolean);
    }
    return false;
  };

  // Main submit handler
  const handleSubmit = async (activeTab, options = {}) => {
    // Check for validation errors from multiple sources
    const hasFieldValidationErrors = hasValidationErrors;
    const hasStoreErrors = hasStoreValidationErrors(activeTab);
    const hasWindowErrors = typeof window !== "undefined" &&
      window.bondValidationErrors ?
      Object.values(window.bondValidationErrors).some(Boolean) :
      false;

    // Prevent submission if any validation errors exist
    if (hasFieldValidationErrors || hasStoreErrors || hasWindowErrors) {
      console.log("Submission blocked due to validation errors:", {
        hasFieldValidationErrors,
        hasStoreErrors,
        hasWindowErrors,
        storeErrors: activeTab === 1 ? bondCharacteristicValidationErrors : bondLadderValidationErrors,
        windowErrors: typeof window !== "undefined" ? window.bondValidationErrors : {}
      });
      return;
    }

    if (activeTab === 0 && !isCusipValid && source !== "allocateAccount") {
      setOrderFieldErrors(prev => ({ ...prev, cusip: true }));
      return;
    }

    let success = false;

    if (activeTab === 0) {
      success = await handleBondOrderSubmit(options);
    } else if (activeTab === 1) {
      success = await handleBondCharacteristicSubmit();
    } else if (activeTab === 2) {
      success = await handleBondLadderSubmit();
    } else if (activeTab === LIST_RFQ_TAB_INDEX) {
      success = await handleListRfqSubmit();
    }

    if (success) {
      resetBondCharacteristicId();
      // Clear all data on success
      clearDataInAllTabs();

      if (onSubmitSuccess) {
        onSubmitSuccess(success, activeTab,
          activeTab === 0 ? actualOrderData :
            activeTab === 1 ? bondCharacteristicData :
              activeTab === 2 ? bondLadderData :
                useListRfqStore.getState().listRfqData);
      }
    }

    return success;
  };

  /**
   * Sends the List RFQ tab as ONE enquiry covering several bonds.
   *
   * In plain terms: check what the trader typed, stamp today's settlement date on every
   * bond, then post the whole lot to /order/list and get back one order per bond.
   *
   * Derived from handleBondOrderSubmit above, which does the same for a single bond: it
   * also validates first, also calls fetchBondCalcSettlementDate at submit time rather
   * than earlier, and also surfaces failures through setError so the footer shows them.
   *
   * Differs in two ways, both forced by the list shape:
   *  - The body is an array, so the response is an array of orders. Success is reported as
   *    the FIRST order's id, because the caller's contract (`success` is a truthy order id)
   *    predates list trading and every other path returns a single id.
   *  - Validation lives in listRfqPayload.validateListRfq rather than inline here, because
   *    it has to report per-row problems as well as per-field ones.
   */
  const handleListRfqSubmit = async () => {
    const { listRfqData, rows: storedRows, setListRfqErrors, setRowErrors } =
      useListRfqStore.getState();

    let rows = storedRows;
    let marketAxessDealers = null;
    if (listRfqData.protocol === PROTOCOL_TYPES.MARKETAXESS) {
      // The tab loads strategy defaults in the background, but Send can be clicked before
      // that request resolves. Fetch definitions here as a submit gate so an untouched list
      // carries the same default parameters the single ticket would send.
      try {
        marketAxessDealers = filterVenueOptions({
          venues: await updateVenues(token, PROTOCOL_TYPES.MARKETAXESS),
          protocol: PROTOCOL_TYPES.MARKETAXESS,
        });
        const types = [...new Set(storedRows.filter((row) => row.cusip || row.qty || row.price)
          .map((row) => row.strategyType).filter(Boolean))];
        const definitions = new Map(await Promise.all(types.map(async (type) =>
          [type, await loadStrategyParamDefs(token, type)])));
        // Re-read after the awaits: an in-flight default fill or trader edit may have updated
        // the row while definitions were loading.
        rows = useListRfqStore.getState().rows.map((row) => {
          const values = { ...(row.strategyParameters || {}) };
          (definitions.get(row.strategyType) || []).forEach((def) => {
            // Preserve an explicitly cleared optional value. The list payload builder
            // omits it, as the single-order create payload does.
            if (!def.paramName || Object.prototype.hasOwnProperty.call(values, def.paramName)) return;
            const raw = def.defaultValue;
            if (raw === null || raw === undefined || raw === '') return;
            const normalized = String(raw).trim().toUpperCase();
            values[def.paramName] = normalized === 'YES' || normalized === 'Y' ? '1'
              : normalized === 'NO' || normalized === 'N' ? '0' : raw;
          });
          return { ...row, strategyParameters: values };
        });
      } catch (error) {
        setError(error?.message || 'Unable to load MarketAxess strategy parameters.');
        return false;
      }
    }

    const { fieldErrors, rowErrors, hasErrors, message } = validateListRfq(listRfqData, rows, marketAxessDealers);
    setListRfqErrors(fieldErrors);
    setRowErrors(rowErrors);

    // The red borders ARE the message, exactly as on the Bond Order tab - handleBondOrderSubmit
    // calls setOrderFieldErrors and returns false without writing anything to the footer. The
    // only thing validateListRfq still puts into words is the empty-list backstop, which has no
    // cell to turn red; setError(null) otherwise clears whatever the previous attempt left.
    setError(message);
    if (hasErrors) return false;

    setError(null);
    setLoading(true);
    try {
      // Same call, same moment in the flow, as the single-bond path.
      const settlementDate = await fetchBondCalcSettlementDate(token);
      const dealerOptionsForList = marketAxessDealers ||
        await updateVenues(token, PROTOCOL_TYPES.TRUMID_RFQ);

      const payload = buildListOrderRequest({
        listRfqData,
        rows,
        settlementDate,
        dealerOptions: dealerOptionsForList || [],
      });

      const createdOrders = await placeListOrder(token, payload);
      const firstOrderId = Array.isArray(createdOrders) && createdOrders.length
        ? createdOrders[0].orderId
        : null;

      if (!firstOrderId) {
        throw new Error("List RFQ was accepted but no orders came back");
      }
      // Close the modal, exactly as handleBondOrderSubmit does on its way out. Each submit
      // handler closes for itself rather than the shared caller doing it, because a handler
      // that returns false must leave the modal open with the trader's input intact.
      if (onClose) onClose();
      return firstOrderId;
    } catch (error) {
      console.error("Failed to place list RFQ:", error);
      setError(error.message || "Failed to place list RFQ. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Bond Order submission
  const handleBondOrderSubmit = async (options = {}) => {
    const trimmedData = trimObjectValues(actualOrderData);

    // NEW: Special handling for allocateAccount source - bypass standard validations
    if (source === "allocateAccount") {
      if (!trimmedData.account) {
        setOrderFieldErrors({ account: true });
        return false;
      }

      setLoading(true);
      setError(null);
      const loadingTimeout = setTimeout(() => {
        setError("Request failed to initiate. Please try again.");
        setLoading(false);
      }, 10000);

      try {
        const response = await allocateTrade(token, initialData.orderId, trimmedData.account, loadingTimeout);
        const success = response && response.orderId ? response.orderId : initialData?.orderId;

        clearDataInAllTabs();
        resetBondCharacteristicId();

        if (onClose) onClose();
        return success;
      } catch (error) {
        console.error("Failed to allocate order:", error);
        setError(error.message || "Failed to allocate order. Please try again.");
        return false;
      } finally {
        setLoading(false);
      }
    }

    const settlementDateFromApi = await fetchBondCalcSettlementDate(token);
    if (settlementDateFromApi) {
      trimmedData.settlementDate = settlementDateFromApi;
      setActualOrderData({ settlementDate: settlementDateFromApi });
    }

    const isEditRfq = options?.editRfqMode || trimmedData.editRfqMode === true;
    const isQtyPriceUpdate = options?.isQtyPriceUpdate === true;
    const requiredFields = isLadderLevelSendToMarket
      ? ["account", "venue", "side"]
      : ["account", "cusip", "qty", "price", "venue", "side"];
    // Sending to market is what takes an order live. handleVenueChange stamps the flag when
    // the trader picks a dealer, but it cannot fire when they simply accept the prefilled
    // dealer, or re-pick the one already showing — which used to leave the draft staged and
    // then, in the previous version of this guard, null out its venue so submit failed
    // validation with no way to recover. The submit gate (isDealerSelectionBlocked) has
    // already refused Trading Desk and any dealer this desk does not trade with, so a venue
    // that survives to here is a real destination. Stamp it live rather than letting the
    // backend fall back to inferring staging from the venue string.
    if (
      source === "sendToMarket" &&
      trimmedData.venue &&
      !isTradingDeskSelected(trimmedData.venue)
    ) {
      trimmedData[STAGING_FLAG_KEY] = STAGING_FLAG_VALUES.LIVE;
    }

    try {
      // Apply RFQ and TruMid RFQ requirements for all users
      if (trimmedData.protocol === PROTOCOL_TYPES.RFQ) {
        requiredFields.push("ttl");
      }
      if (trimmedData.protocol === PROTOCOL_TYPES.TRUMID_RFQ) {
        requiredFields.push("rfqInquiryType", "quoteSubmissionDeadline", "priceType", "allowPartialExecution");
        if (trimmedData.rfqInquiryType === "BIN") {
          requiredFields.push("ttl");
        }
        if (trimmedData.priceType === "SPREAD") {
          requiredFields.push("stipulationType");
        }
      }

      // MarketAxess validation - uses shared rfqInquiryType and priceType keys
      if (trimmedData.protocol === PROTOCOL_TYPES.MARKETAXESS) {
        requiredFields.push(
          "mktxDealerListMode",
          "rfqInquiryType",
          "priceType",
          "mktxPricingProcess",
          "strategyType",
          "effectiveTime",
          "expiryTime"
        );

        // NEW: Remove venue and mktxDealerListMode from required if strategy type is SEEK or PROVIDE
        const isSeekOrProvide =
          trimmedData.strategyType === MKTX_STRATEGY_TYPES.SEEK ||
          trimmedData.strategyType === MKTX_STRATEGY_TYPES.PROVIDE;

        if (isSeekOrProvide) {
          // Remove venue requirement
          const venueIndex = requiredFields.indexOf("venue");
          if (venueIndex > -1) {
            requiredFields.splice(venueIndex, 1);
          }
          // Remove mktxDealerListMode requirement
          const dealerListIndex = requiredFields.indexOf("mktxDealerListMode");
          if (dealerListIndex > -1) {
            requiredFields.splice(dealerListIndex, 1);
          }
          // NEW: Ensure mktxDealerListMode is null when hidden
          trimmedData.mktxDealerListMode = null;
        } else {
          // Original logic: remove venue as required when market axess
          const venueIndex = requiredFields.indexOf("venue");
          if (venueIndex > -1) {
            requiredFields.splice(venueIndex, 1);
          }

          // Add venue back if mode is INCLUDE_ONLY
          if (trimmedData.mktxDealerListMode === MKTX_DEALER_LIST_MODES.INCLUDE_ONLY) {
            requiredFields.push("venue");
          }
        }
      }
    } catch (error) {
      console.error("Error processing protocol requirements:", error);
    }

    // NEW: Prevent removing/emptying original strategy params when strategy type is unchanged in edit-RFQ mode
    const guardMissingStrategyParam = (() => {
      if (!isEditRfq || trimmedData.protocol !== PROTOCOL_TYPES.MARKETAXESS) return null;
      const origType = trimmedData.strategyTypeOriginal;
      const currType = trimmedData.strategyType;
      if (!origType || !currType || origType !== currType) return null;

      const normalize = (v) => {
        if (v === null || v === undefined) return "";
        const s = String(v).trim();
        if (!s) return "";
        const upper = s.toUpperCase();
        if (upper === "YES") return "Y";
        if (upper === "NO") return "N";
        return s;
      };

      const originals = trimmedData.strategyParametersOriginal || {};
      const current = trimmedData.strategyParameters || {};
      for (const [key, origVal] of Object.entries(originals)) {
        const origNorm = normalize(origVal);
        if (!origNorm) continue; // ignore originally empty values
        const currNorm = normalize(current[key]);
        if (!currNorm) {
          return key; // found a removed/emptied required param
        }
      }
      return null;
    })();

    if (guardMissingStrategyParam) {
      const paramErrors = { [guardMissingStrategyParam]: true };
      setOrderFieldErrors({ strategyParameters: paramErrors });
      setModalError(
        `Strategy parameter "${guardMissingStrategyParam}" cannot be removed or left empty when the strategy type is unchanged. Please provide a value or change the strategy type.`,
        "strategyParameters"
      );
      return false;
    }

    let newErrors = {};
    requiredFields.forEach((field) => {
      if (!trimmedData[field] || trimmedData[field].toString().trim() === "") {
        newErrors[field] = true;
      }
    });

    // Enforce minQty when provided (only for inventory source)
    if (source === "inventory" && trimmedData.minQty != null) {
      const min = Number(trimmedData.minQty);
      const qtyVal = Number(trimmedData.qty);
      if (Number.isFinite(min) && Number.isFinite(qtyVal) && qtyVal < min) {
        newErrors.qty = true;
        setOrderFieldErrors(newErrors);
        // setModalError associates an error message with an optional field name
        setModalError(`Order quantity cannot be less than the specified minimum bond quantity.`, 'qty');
        return false;
      }
    }

    // MarketAxess: qty must be a multiple of 1000
    if (!isLadderLevelSendToMarket && trimmedData.protocol === PROTOCOL_TYPES.MARKETAXESS) {
      const qtyVal = Number(trimmedData.qty);
      if (Number.isFinite(qtyVal) && qtyVal % 1000 !== 0) {
        newErrors.qty = true;
        setOrderFieldErrors(newErrors);
        setModalError(`For MarketAxess, quantity must be a multiple of 1000.`, 'qty');
        return false;
      }
    }

    // Validate TTL for regular RFQ
    if (trimmedData.protocol === "RFQ" && trimmedData.ttl) {
      const ttlValue = Number(trimmedData.ttl);
      if (isNaN(ttlValue) || ttlValue < 60 || ttlValue > 3000) {
        newErrors.ttl = true;
      }
    }

    // Validate TruMid RFQ specific fields (now stored in MINUTES)
    if (trimmedData.protocol === PROTOCOL_TYPES.TRUMID_RFQ) {
      if (trimmedData.quoteSubmissionDeadline !== "" && trimmedData.quoteSubmissionDeadline !== null && trimmedData.quoteSubmissionDeadline !== undefined) {
        const minutes = Number(trimmedData.quoteSubmissionDeadline);
        if (isNaN(minutes) || !Number.isInteger(minutes) || minutes < 2 || minutes > 30) {
          newErrors.quoteSubmissionDeadline = true;
        }
      }
      if (trimmedData.rfqInquiryType === "BIN" && trimmedData.ttl !== "" && trimmedData.ttl !== null && trimmedData.ttl !== undefined) {
        const minutes = Number(trimmedData.ttl);
        if (isNaN(minutes) || !Number.isInteger(minutes) || minutes < 2 || minutes > 30) {
          newErrors.ttl = true;
        }
      }
    }

    // MarketAxess field validations
    if (trimmedData.protocol === PROTOCOL_TYPES.MARKETAXESS) {
      // Removed quoteSubmissionDeadline validation
      // No additional integer-field validations for removed fields
    }

    // MarketAxess: Auto-fill past times and validate effective > expiry
    if (trimmedData.protocol === PROTOCOL_TYPES.MARKETAXESS) {

      const effMs = parseUtcMs(trimmedData.effectiveTime);
      const expMs = parseUtcMs(trimmedData.expiryTime);
      const nowUtc = Date.now();

      if (!isEditRfq) {
        // Only update expiryTime if it's in the past
        if (Number.isFinite(expMs) && expMs <= nowUtc) {
          trimmedData.expiryTime = getSameDayMarketAxessExpiryIso();
        }

        // Only update effectiveTime if it's in the past, using min(now UTC, expiry UTC)
        if (Number.isFinite(effMs) && effMs <= nowUtc) {
          trimmedData.effectiveTime = getDefaultEffectiveFromExpiry(trimmedData.expiryTime);
        }
      }

      // Always block if effective > expiry (for both edit and non-edit)
      const finalEffMs = parseUtcMs(trimmedData.effectiveTime);
      const finalExpMs = parseUtcMs(trimmedData.expiryTime);
      if (Number.isFinite(finalEffMs) && Number.isFinite(finalExpMs) && finalEffMs > finalExpMs) {
        newErrors.effectiveTime = true;
        newErrors.expiryTime = true;
        setModalError("Effective time cannot be after expiry time.", "effectiveTime");
        return false;
      }
    }

    // MarketAxess strategy parameter validation
    if (trimmedData.protocol === PROTOCOL_TYPES.MARKETAXESS) {
      const defs = trimmedData.strategyParamDefinitions || [];
      const vals = trimmedData.strategyParameters || {};
      const paramErrors = {};
      defs.forEach((def) => {
        const meta = deriveParamMeta(def);
        const val = vals[def.paramName];
        const missing = val === null || val === undefined || val === "";
        // Only validate if value is provided (not missing)
        const invalid = !missing && !validateParamValue(meta, val);
        if (invalid) {
          paramErrors[def.paramName] = true;
        }
      });
      if (Object.keys(paramErrors).length > 0) {
        newErrors.strategyParameters = paramErrors;
      }
    }

    const ladderListName = Object.prototype.hasOwnProperty.call(options, "listName")
      ? options.listName?.trim() || null
      : trimmedData.protocol === PROTOCOL_TYPES.MARKETAXESS
        ? trimmedData.listName?.trim() || null
        : null;
    // The optional MA ladder list name uses the same application limit as List RFQ.
    // Ignore this ticket-only field for single releases and other protocols.
    if (isLadderLevelSendToMarket && options?.sendAsList === true &&
        trimmedData.protocol === PROTOCOL_TYPES.MARKETAXESS &&
        (ladderListName || "").length > MAX_LIST_RFQ_NAME_LENGTH) {
      newErrors.listName = true;
      setModalError(`List name can be at most ${MAX_LIST_RFQ_NAME_LENGTH} characters.`, "listName");
    }

    if (Object.keys(newErrors).length > 0) {
      setOrderFieldErrors(newErrors);
      if (newErrors.strategyParameters) {
        setModalError("Please correct invalid strategy parameter values.", "strategyParameters");
      }
      return false;
    }

    setOrderFieldErrors({});
    setLoading(true);
    setError(null);

    const loadingTimeout = setTimeout(() => {
      setError("Request failed to initiate. Please try again.");
      setLoading(false);
    }, 10000);

    // use same allowed-fields list for both full submit and qty-update
    const dataToSubmit = pick(trimmedData, ORDER_FIELDS_TO_SEND);
    if (isLadderLevelSendToMarket) {
      delete dataToSubmit.cusip;
      delete dataToSubmit.qty;
      delete dataToSubmit.price;
      delete dataToSubmit.bondInventoryId;
      dataToSubmit.secondarySource = secondarySource;
      dataToSubmit.orderId = null;
      // "Send List Order" rather than "Send Order": the same staged bonds, sent to the selected venue as one
      // inquiry instead of one per bond. The backend reads this only on this exact call -
      // handleBondLadderCommand, reached because bondLadderId is set and orderId is null - and
      // still finds the bonds, merges these settings onto them and updates those same rows. All
      // this flag changes is whether it emits one list message (Trumid 35=R, MA 35=E) or singles.
      //
      // Set only when true, so the normal Send sends precisely the payload it always has.
      if (options?.sendAsList === true) {
        dataToSubmit.sendAsList = true;
        // Name prompt is authoritative, including an explicitly cleared name.
        // Direct MA ticket submissions fall back to the existing ticket field.
        dataToSubmit.listName = ladderListName;
        // MA Include At Least permits no selected dealers; an empty selection still sends live.
        if (dataToSubmit.protocol === PROTOCOL_TYPES.MARKETAXESS) {
          dataToSubmit[STAGING_FLAG_KEY] = STAGING_FLAG_VALUES.LIVE;
        }
      }
    } else {
      dataToSubmit.orderId = initialData?.orderId || null;
    }

    if (dataToSubmit.venue && Array.isArray(dataToSubmit.venue)) {
      dataToSubmit.venue.sort((a, b) => a.localeCompare(b));
    }

    if (dataToSubmit.protocol === PROTOCOL_TYPES.RFQ && Array.isArray(dataToSubmit.venue)) {
      dataToSubmit.venue = dataToSubmit.venue.join(",");
    }

    // TruMid RFQ specific handling (convert minutes -> seconds here only)
      if (dataToSubmit.protocol === PROTOCOL_TYPES.TRUMID_RFQ) {
      // The List Order tab can load MarketAxess dealers after this ticket loaded Trumid's.
      // Resolve against Trumid's own cached options at send time, not the shared last-loaded
      // fieldOptions.venue, so switching tabs cannot turn a partial selection into ALL.
      const trumidDealerOptions = await updateVenues(token, PROTOCOL_TYPES.TRUMID_RFQ);
      // dataToSubmit.venue = "ALL";
      //if all the venues are selected, that is whatever number venues we received from backend,
      // same number venues are selected by user we send "ALL" to backend. If user selects specific venues, we send those venues as comma separated string.
      if (Array.isArray(trimmedData.venue) && Array.isArray(trumidDealerOptions)) {
        const normalizedSelection = trimmedData.venue
          .map((venue) => String(venue).trim())
          .filter((venue) => venue);

        const hasAllSelected = normalizedSelection.includes("ALL");

        const selectedDealers = normalizedSelection
          .filter((venue) => venue !== "ALL")
          .sort((a, b) => a.localeCompare(b));

        const availableDealers = (trumidDealerOptions || [])
          .map((venue) => String(venue).trim())
          .filter((venue) => venue);

        const allDealersSelected =
          availableDealers.length > 0 &&
          availableDealers.every((dealer) => selectedDealers.includes(dealer));

        if (hasAllSelected || allDealersSelected) {
          dataToSubmit.venue = "ALL";
        } else {
          dataToSubmit.venue = selectedDealers.join(",");
        }
      }

      const toSeconds = (m) =>
        m === null || m === undefined || m === "" ? null : Number(m) * 60;

      const normalizedAllowPartial = normalizeYesNoToYN(dataToSubmit.allowPartialExecution);
      dataToSubmit.allowPartialExecution =
        normalizedAllowPartial === "" ? null : normalizedAllowPartial;

      // TTL is only applicable for BIN; default to 5 minutes if missing
      if (dataToSubmit.rfqInquiryType === "BIN") {
        const ttlMinutes = dataToSubmit.ttl ?? 5;
        dataToSubmit.ttl = toSeconds(ttlMinutes);
      } else {
        dataToSubmit.ttl = null;
      }

      // Quote submission deadline default 5 minutes if missing
      dataToSubmit.quoteSubmissionDeadline = toSeconds(
        dataToSubmit.quoteSubmissionDeadline ?? 5
      );

      // Format settlement date to yyyy-mm-dd
      if (dataToSubmit.settlementDate) {
        const date = new Date(dataToSubmit.settlementDate);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        dataToSubmit.settlementDate = `${yyyy}-${mm}-${dd}`;
      }

      // Set stipulationType to null if priceType is not SPREAD
      if (dataToSubmit.priceType !== "SPREAD") {
        dataToSubmit.stipulationType = null;
      }
    } else if (dataToSubmit.protocol === PROTOCOL_TYPES.MARKETAXESS) {
      // MarketAxess specific handling
      // Note: Uses shared rfqInquiryType and priceType keys
      if (Array.isArray(dataToSubmit.venue)) {
        dataToSubmit.venue = dataToSubmit.venue.join(",");
      }

      // Removed quoteSubmissionDeadline conversion
      dataToSubmit.stipulationType = null;
      dataToSubmit.allowPartialExecution = null;
      dataToSubmit.ttl = null;
      dataToSubmit.quoteSubmissionDeadline = null; // Ensure it's null

      // Ensure rfqInquiryType is BIN for MarketAxess submissions
      dataToSubmit.rfqInquiryType = dataToSubmit.rfqInquiryType || MKTX_INQUIRY_TYPES.BIN;

      dataToSubmit.mktxDealerListMode =
        MKTX_DEALER_LIST_MODE_DISPLAY_TO_SUBMIT[dataToSubmit.mktxDealerListMode] ||
        dataToSubmit.mktxDealerListMode;
      // Preserve user-entered times as trimmed strings (no timezone conversion)
      dataToSubmit.effectiveTime = trimmedData.effectiveTime
        ? String(trimmedData.effectiveTime).trim()
        : null;
      dataToSubmit.expiryTime = trimmedData.expiryTime
        ? String(trimmedData.expiryTime).trim()
        : null;
      // map strategyParameters -> strategyParams array with paramId/value
      const defs = trimmedData.strategyParamDefinitions || [];
      const vals = filterStrategyValues(defs, trimmedData.strategyParameters || {});
      if (isEditRfq) {
        dataToSubmit.strategyParams = buildStrategyParamsDelta(
          defs,
          vals,
          trimmedData.strategyParamsExisting || []
        );
      } else {
        dataToSubmit.strategyParams = defs
          .filter((d) => {
            const paramName = d?.paramName;
            const paramValue = paramName ? vals[paramName] : undefined;
            return paramName && paramValue !== undefined && paramValue !== null && paramValue !== "";
          })
          .map((d) => ({
            paramId: d.id,
            paramName: d.paramName,
            dataType: d.dataType,
            value: vals[d.paramName],
          }));
      }
      delete dataToSubmit.strategyParameters;
      delete dataToSubmit.strategyParamDefinitions;
    } else {
      // Clear all RFQ and MarketAxess fields for other protocols (CLOB, regular RFQ)
      dataToSubmit.rfqInquiryType = null;
      dataToSubmit.settlementDate = null;
      dataToSubmit.priceType = null;
      dataToSubmit.stipulationType = null;
      dataToSubmit.allowPartialExecution = null;
      dataToSubmit.quoteSubmissionDeadline = null;  // Note: This is now cleared for non-RFQ protocols, but used for MarketAxess
      // Clear MarketAxess specific fields
      dataToSubmit.mktxDealerListMode = null;
      dataToSubmit.mktxTargetLevel = null;
      dataToSubmit.mktxPricingProcess = null;
      dataToSubmit.strategyType = null;
      dataToSubmit.effectiveTime = null;
      dataToSubmit.expiryTime = null;
      dataToSubmit.strategyParameters = undefined;
      dataToSubmit.strategyParams = undefined;
    }

    // For edit RFQ, reduce to minimal DTO
    const payloadForSubmit =
      isEditRfq && trimmedData.protocol === PROTOCOL_TYPES.MARKETAXESS
        ? pick(dataToSubmit, MODIFY_RFQ_FIELDS)
        : dataToSubmit;

    try {
      let response;
      // For qty-update we still call editOrder (modify existing order)
      if (isEditRfq) {
        const rfqIdForEdit =
          trimmedData.rfqId || initialData?.rfqId || trimmedData.orderId;
        if (!rfqIdForEdit) {
          throw new Error("RFQ identifier missing for edit");
        }
        response = await modifyRfq(token, rfqIdForEdit, payloadForSubmit, loadingTimeout);
      } else if (isQtyPriceUpdate || source === "sendToMarket") {
        const orderModifyId = isLadderLevelSendToMarket
          ? initialData?.orderId ?? initialData?.bondLadderId
          : initialData?.orderId;
        if (!orderModifyId) {
          throw new Error(isLadderLevelSendToMarket ? "Bond ladder ID missing for send to market" : "Order ID missing for send to market");
        }
        response = await editOrder(token, dataToSubmit, orderModifyId, loadingTimeout);
      } else {
        console.log("Placing new order with data:", dataToSubmit);
        response = await placeOrder(token, dataToSubmit, loadingTimeout);
      }

      const success = response && response.orderId ? response.orderId : initialData?.orderId;

      // Clear all data after successful submission
      clearDataInAllTabs();
      resetBondCharacteristicId();

      if (onClose) onClose();
      return success;
    } catch (error) {
      console.error(
        (source === "sendToMarket" || source === "allocateAccount") // NEW
          ? "Failed to edit order:"
          : "Failed to place order:", error
      );
      setError(error.message || ((source === "sendToMarket")
        ? "Failed to edit order. Please try again."
        : source === "allocateAccount" ? "Failed to allocate order. Please try again." // NEW
          : "Failed to place order. Please try again."));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Bond Characteristic submission
  const handleBondCharacteristicSubmit = async () => {
    const trimmedData = trimObjectValues(bondCharacteristicData);
    const requiredFields = ["side", "account", "qty", "bondType"];

    // No PM-specific fields needed anymore

    let newErrors = {};
    requiredFields.forEach((field) => {
      if (!trimmedData[field] || trimmedData[field].toString().trim() === "") {
        newErrors[field] = true;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setBondCharacteristicErrors(newErrors);
      return false;
    }

    trimmedData.maturityMin = trimmedData.maturityMin
      ? trimmedData.maturityMin + "Y"
      : trimmedData.maturityMin;
    trimmedData.maturityMax = trimmedData.maturityMax
      ? trimmedData.maturityMax + "Y"
      : trimmedData.maturityMax;

    // NEW: proactive prune of unsupported fields before generic submission
    const prunedData = {
      ...trimmedData,
      // only keep allowed characteristic fields via filterObjectByBondType
      ...filterObjectByBondType(trimmedData.bondType, trimmedData),
    };

    setBondCharacteristicErrors({});

    return await processBondSubmission({
      data: prunedData,
      storeChecked: useBondCharacteristicStore.getState().checked,
      panelName: "bondCharacteristic",
      createBond: createBondCharacteristic,
      requiredFields,
      errorMsgMissing: "Please fill all required fields for Bond Characteristics.",
    });
  };

  // Bond Ladder submission
  const handleBondLadderSubmit = async () => {
    const trimmedData = trimObjectValues(bondLadderData);
    const requiredFields = [
      "side", "account", "qty", "bondType",
      "nameOfTheLadder", "numberOfRungs", "amountPerRung", "totalMaturity",
    ];

    // No PM-specific fields needed anymore

    let newErrors = {};
    requiredFields.forEach((field) => {
      if (!trimmedData[field] || trimmedData[field].toString().trim() === "") {
        newErrors[field] = true;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setBondLadderErrors(newErrors);
      return false;
    }

    // NEW: proactive prune of unsupported fields before generic submission
    const prunedData = {
      ...trimmedData,
      ...filterObjectByBondType(trimmedData.bondType, trimmedData),
    };

    setBondLadderErrors({});

    return await processBondSubmission({
      data: prunedData,
      storeChecked: useBondLadderStore.getState().checked,
      panelName: "bondLadder",
      createBond: createBondLadder,
      requiredFields,
      errorMsgMissing: "Please fill all required fields for Bond Ladder.",
    });
  };

  // Helper to build strategy param add/update/remove delta for edit RFQ
  const buildStrategyParamsDelta = (defs = [], values = {}, existing = []) => {
    const allowedNames = new Set(defs.map((d) => d.paramName).filter(Boolean));
    const defsByName = defs.reduce((acc, def) => {
      if (def?.paramName) acc[def.paramName] = def;
      return acc;
    }, {});

    // Index existing by paramName (even those not in current defs, so we can remove them)
    const existingByName = {};
    existing.forEach((p) => {
      if (p?.paramName) {
        existingByName[p.paramName] = p;
      }
    });

    const add = [];
    const update = [];
    const remove = [];

    // Add/Update only for allowed params
    defs.forEach((def) => {
      if (!def?.paramName || !allowedNames.has(def.paramName)) return;
      const val = values[def.paramName];
      const prev = existingByName[def.paramName];
      const mappingId = prev?.mappingId ?? prev?.paramId ?? prev?.id;

      const isMissing = val === undefined || val === null || val === "";
      if (isMissing) {
        if (mappingId) remove.push({ paramId: mappingId });
        return;
      }

      if (!prev) {
        add.push({
          paramId: def.id,
          paramName: def.paramName,
          dataType: def.dataType,
          value: val,
        });
      } else if (mappingId) {
        const prevVal = prev.paramValue ?? prev.value;
        if (String(prevVal) !== String(val)) {
          update.push({
            paramId: mappingId,
            paramName: def.paramName,
            dataType: def.dataType,
            value: val,
          });
        }
      }
    });

    // Remove anything from the original that is NOT mapped to the current strategy type
    Object.keys(existingByName).forEach((key) => {
      const prev = existingByName[key];
      const mappingId = prev?.mappingId ?? prev?.paramId ?? prev?.id;
      if (!allowedNames.has(key)) {
        if (mappingId) {
          remove.push({
            paramId: mappingId,
            paramName: key,
            dataType: defsByName[key]?.dataType || prev?.dataType || null,
          });
        }
        return;
      }
      if (!(key in values) && mappingId) {
        remove.push({
          paramId: mappingId,
          paramName: key,
          dataType: defsByName[key]?.dataType || prev?.dataType || null,
        });
      }
    });

    return { add, update, remove };
  };

  // Helper to filter values to current strategy definitions
  const filterStrategyValues = (defs = [], values = {}) => {
    const allowed = new Set(defs.map((d) => d.paramName));
    return Object.entries(values || {}).reduce((acc, [k, v]) => {
      if (allowed.has(k)) acc[k] = v;
      return acc;
    }, {});
  };

  // setModalError associates an error message with an optional field name
  const setModalError = (msg, fieldName = null) => {
    setError(msg);
    setLastErrorField(fieldName);
  };

  // clearModalError will clear the modal error only if the provided field
  // matches the lastErrorField (or if no field provided, clears unconditionally)
  const clearModalError = (fieldName = null) => {
    if (!fieldName || lastErrorField === fieldName) {
      setError(null);
      setLastErrorField(null);
    }
  };

  return {
    // State
    isCusipValid,
    setIsCusipValid,
    bondDetailsMain,
    setBondsDetailsMain,
    showBondsDetails,
    setShowBondsDetails,
    showConfirmation,
    setShowConfirmation,
    accountIdentifiers,
    error,
    setModalError,
    clearModalError,
    setError,
    loading,
    setLoading,
    hasValidationErrors,
    setHasValidationErrors,

    // Store data
    actualOrderData,
    setActualOrderData,
    bondCharacteristicData,
    bondLadderData,

    // Field errors
    orderFieldErrors,
    bondCharacteristicErrors,
    bondLadderErrors,

    // Validation errors from stores
    bondCharacteristicValidationErrors,
    bondLadderValidationErrors,

    // Actions
    handleSubmit,
    handleClearAll,
    clearDataInAllTabs,

    // Token and role
    token,
    tokenRole,
  };
};
